import { twMerge } from 'tailwind-merge';
import { useDispatch } from '@witmetrics/data-store';
import type { DecoratedUnisonProjectTask } from '@witmetrics/utils';
import { formatUserName } from '@witmetrics/utils/strings';
import { API } from '@/api';
import { useToggle } from '@/hooks/useToggle';
import { usePath } from '@/hooks/usePath';
import { useAppState } from '@/providers/AppStateProvider';
import { CardListItem } from '@/components/List';
import CheckBox from '@/components/CheckBox';
import DefaultOptionsMenu from '@/components/DefaultOptionsMenu';
import ProfilePicture from '@/components/ProfilePicture';
import Tooltip from '@/components/Tooltip';
import UserChip from '@/components/UserChip';
import DeleteTaskDialog from '@/dialogs/DeleteTaskDialog';
import UnisonProjectTaskDialog from '@/dialogs/UnisonProjectTaskDialog';
import TaskNotes from './TaskNotes';
import UnisonProjectTaskHeader from './UnisonProjectTaskHeader';
import { classes, isHighlighted } from './utils';

export type UnisonProjectTaskListItemProps = {
  condensed?: boolean;
  onlyTime?: boolean;
  className?: string;
  unisonProjectTask: DecoratedUnisonProjectTask;
};

export default function UnisonProjectTaskListItem({
  condensed,
  onlyTime,
  className,
  unisonProjectTask,
}: UnisonProjectTaskListItemProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const { searchParams } = usePath();
  const [isEditDialogShown, toggleEditDialog] = useToggle();
  const [isDeleteWarningShown, toggleDeleteWarning] = useToggle();
  const [isUpdating, toggleUpdating] = useToggle();
  const { task, unisonProject } = unisonProjectTask;

  const handleEditTask = () => {
    toggleEditDialog(true);
  };

  const handleCancelEdit = () => {
    toggleEditDialog(false);
  };

  const handleShowDeleteWarning = () => {
    toggleDeleteWarning();
  };

  const handleToggleChecked = async (isResolved: boolean) => {
    try {
      toggleUpdating(true);
      dispatch.addTasks([await API.Tasks.updateTask(task.id, { isResolved })]);
      toggleUpdating(false);
    } catch (err) {
      toggleUpdating(false);
      onApiError(err, 'Error saving task');
    }
  };

  const renderAssignee = () =>
    condensed ? (
      <Tooltip title={formatUserName(task.assignedTo)}>
        <div>
          <ProfilePicture
            className={classes.profilePicture}
            size={25}
            user={task.assignedTo}
          />
        </div>
      </Tooltip>
    ) : (
      <UserChip maxLength={20} user={task.assignedTo} />
    );

  return (
    <>
      {isEditDialogShown && (
        <UnisonProjectTaskDialog
          taskId={task.id}
          projectId={unisonProject.id}
          onClose={handleCancelEdit}
        />
      )}
      {isDeleteWarningShown && (
        <DeleteTaskDialog
          taskId={task.id}
          onClose={() => toggleDeleteWarning()}
        />
      )}
      <CardListItem
        className={twMerge(
          classes.card,
          isHighlighted(unisonProjectTask, searchParams) && classes.highlighted,
          className
        )}
        renderIcon={() => (
          <CheckBox
            isUpdating={isUpdating}
            isChecked={task.isResolved}
            name="isResolved"
            variant="light"
            onChange={({ isResolved }) => handleToggleChecked(isResolved)}
          />
        )}
        renderMenu={(open, ref, onClose) => (
          <DefaultOptionsMenu
            anchorTo={ref}
            open={open}
            onEdit={handleEditTask}
            onDelete={handleShowDeleteWarning}
            onClose={onClose}
          />
        )}>
        <div className={classes.content}>
          <div className={classes.textColumn}>
            <UnisonProjectTaskHeader
              onlyTime={onlyTime}
              unisonProjectTask={unisonProjectTask}
            />
            <TaskNotes task={task} />
          </div>
          {renderAssignee()}
        </div>
      </CardListItem>
    </>
  );
}
